/* eslint-disable jsx-a11y/label-has-associated-control */

/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { useEffect, useMemo, useState } from 'react'
import { Calendar } from 'react-date-range'
import { ChevronLeft, ChevronRight, AlertTriangle, ChevronDown } from '@untitled-ui/icons-react'
import { format, isAfter, isBefore, isSameDay, isWithinInterval, parse } from 'date-fns'
import { TimePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { TextField } from '@mui/material'
import MyButton from '../Button/MyButton'
import MyTextField from '../TextField/MyTextField'
import MyPopper from '../Popper/MyPopper'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function MyCalendar({ target, value = null, showTime, minDate, maxDate, onChange, control }) {
  const [isInvalidDate, setIsInvalidDate] = useState(false)
  const [inputDate, setInputDate] = useState(value && format(value, 'MMM d, yyyy'))
  const [date, setDate] = useState()
  const [time, setTime] = useState(dayjs()) // Initialize with some default value

  const isDisabledToday = useMemo(() => {
    let disabled = false

    if (minDate && maxDate) {
      disabled = !isWithinInterval(new Date(), {
        start: minDate,
        end: maxDate,
      })
    } else if (minDate) {
      disabled = !isAfter(new Date(), minDate)
    } else if (maxDate) {
      disabled = !isBefore(new Date(), maxDate)
    }

    return disabled
  }, [minDate, maxDate])

  // Year dropdown range for the header. Bounded by minDate/maxDate when supplied
  // (e.g. a DOB field caps at today via maxDate); otherwise spans current year
  // ±100. Without this, navigating decades back for a DOB takes hundreds of
  // chevron clicks — the pain point this dropdown fixes.
  const yearOptions = useMemo(() => {
    const now = new Date()
    const minYear = minDate ? minDate.getFullYear() : now.getFullYear() - 100
    const maxYear = maxDate ? maxDate.getFullYear() : now.getFullYear() + 10
    const years = []
    for (let y = maxYear; y >= minYear; y--) years.push(y)
    return years
  }, [minDate, maxDate])

  const timeXs = {
    // Styles for the TextField
    '& .MuiInputBase-root': {
      borderRadius: '8px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      boxShadow: '0px 1px 2px 0px #1018280D',
      backgroundColor: '#FFFFFF',
      border: '1px solid #D0D5DD',
      '&.Mui-error': { border: '1px solid #FDA29B' },
      '& .MuiInputBase-input::placeholder': {
        fontSize: '16px',
        fontFamily: "'Inter', sans-serif",
        fontWeight: '400',
        lineHeight: '24px',
        wordWrap: 'break-word',
        color: '#667085',
        opacity: 1,
      },
      '&.Mui-focused': {
        // border: '1px solid #8ED8F6',
        // boxShadow: '0px 0px 0px 4px #72C8EB3D',
        boxShadow: `inset 0 0 0 2px #365CC3`,
      },
      '&.Mui-focused.Mui-error': {
        border: '1px solid #FDA29B',
        boxShadow: '0px 0px 0px 4px #F044383D, 0px 1px 2px 0px #1018280D',
      },
      '& .MuiInputBase-input': {
        padding: '0px',
        margin: '10px 14px',
        textAlign: 'left',
        appearance: 'none',
      },
      '&.Mui-disabled': { backgroundColor: '#F9FAFB', cursor: 'not-allowed' },
      '&.Mui-disabled .MuiInputBase-input': {
        WebkitTextFillColor: '#667085',
        color: '#667085',
      },
      '& .MuiInputAdornment-root': {
        height: '100%',
        maxHeight: 'unset',
        paddingTop: '0px',
        paddingBottom: '0px',
        margin: '0px',
      },
      '& fieldset': { padding: 0, border: 'none' },
      '&:hover fieldset': { border: 'none' },
      '&.Mui-focused fieldset': { border: 'none' },
      '&.Mui-disabled fieldset': { border: 'none' },
    },
    // Styles for the FormHelperText
    '& .MuiFormHelperText-root': {
      margin: '0px !important',
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '20px',
      wordWrap: 'break-word',
      color: '#475467',
      '&.Mui-error': { color: '#D92D20' },
    },
  }

  useEffect(() => {
    if (date) {
      setInputDate(format(date, 'MMM d, yyyy'))
      setIsInvalidDate(false)
    }
  }, [date])
  useEffect(() => {
    if (value) {
      setDate(new Date(value))

      showTime && setTime(dayjs(value))
      setInputDate(format(value, 'MMM d, yyyy'))
    }
  }, [value])

  return (
    <MyPopper target={target} placement="bottom-start">
      {(open, anchorEl, show, close) => (
        <div className="flex w-[328px] flex-col">
          <div className="flex flex-col gap-3 px-6 py-5">
            <Calendar
              months={1}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(item) => setDate(item)}
              date={date}
              navigatorRenderer={(focusedDate, changeShownDate, props) => (
                <div className="flex flex-col gap-3 pb-3" onMouseUp={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => changeShownDate(-1, 'monthOffset')}
                      className="flex h-10 w-10 shrink-0 items-center justify-center p-2"
                    >
                      <ChevronLeft className="size-5" stroke="currentColor" />
                    </button>
                    <div className="flex flex-1 items-center justify-center gap-2">
                      <HeaderSelect
                        value={focusedDate.getMonth()}
                        onChange={(month) => {
                          const next = new Date(focusedDate)
                          next.setDate(1) // avoid overflow when switching e.g. Jan 31 → Feb
                          next.setMonth(month)
                          changeShownDate(next)
                        }}
                        options={MONTHS.map((m, i) => ({ value: i, label: m }))}
                        ariaLabel="Month"
                      />
                      <HeaderSelect
                        value={focusedDate.getFullYear()}
                        onChange={(year) => {
                          const next = new Date(focusedDate)
                          next.setDate(1)
                          next.setFullYear(year)
                          changeShownDate(next)
                        }}
                        options={yearOptions.map((y) => ({ value: y, label: String(y) }))}
                        ariaLabel="Year"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => changeShownDate(+1, 'monthOffset')}
                      className="flex h-10 w-10 shrink-0 items-center justify-center p-2"
                    >
                      <ChevronRight className="size-5" stroke="currentColor" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <MyTextField
                      value={inputDate}
                      isRealtime
                      placeholder="MMM d, yyyy"
                      isError={isInvalidDate}
                      onChangeForm={(e) => setInputDate(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const parsedDate = parse(e.target.value, 'MMM d, yyyy', new Date())

                          let valid = true
                          if (isNaN(parsedDate.getTime())) valid = false

                          if (
                            minDate &&
                            maxDate &&
                            !isSameDay(parsedDate, minDate) &&
                            !isSameDay(parsedDate, maxDate)
                          ) {
                            valid = isWithinInterval(parsedDate, {
                              start: minDate,
                              end: maxDate,
                            })
                          } else if (minDate && !isSameDay(parsedDate, minDate)) {
                            valid = isAfter(parsedDate, minDate)
                          } else if (maxDate && !isSameDay(parsedDate, maxDate)) {
                            valid = isBefore(parsedDate, maxDate)
                          }

                          if (valid) {
                            changeShownDate(parsedDate)
                            setDate(parsedDate)
                          } else {
                            setIsInvalidDate(true)
                          }
                        }
                      }}
                      endAdornment={
                        isInvalidDate && (
                          <AlertTriangle className="size-5 text-error/700" stroke="currentColor" />
                        )
                      }
                    />
                    <MyButton
                      disabled={isDisabledToday}
                      color="secondary"
                      variant="outlined"
                      size="md"
                      onClick={() => {
                        if (!isDisabledToday) {
                          const now = new Date()
                          changeShownDate(now)
                          setDate(now)
                        }
                      }}
                    >
                      <p className="text-sm-semibold">Today</p>
                    </MyButton>
                  </div>
                </div>
              )}
              showMonthAndYearPickers={false}
              weekStartsOn={1}
              weekdayDisplayFormat="EEEEEE"
              rangeColors={['#55B3D9']}
              color="#55B3D9"
            />
            {showTime && (
              <div className="flex w-full justify-center">
                <div className="flex flex-col gap-0.5">
                  <TimePicker
                    value={time}
                    ampm={false}
                    sx={timeXs}
                    slotProps={{
                      textField: {
                        error: false,
                      },
                    }}
                    onChange={(newValue) => {
                      console.log('onChange', newValue)
                      setTime(dayjs(newValue))
                    }}
                  />
                  <div>
                    <label className="text-sm-regular text-[#aeb9bf]">
                      * Format time is 24 hours
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          <footer className="flex items-center gap-3 border-t border-gray-light/200 p-4">
            <div className="flex-1">
              <MyButton expanded color="secondary" variant="outlined" size="md" onClick={close}>
                <p className="text-sm-semibold">Cancel</p>
              </MyButton>
            </div>
            <div className="flex-1">
              <MyButton
                expanded
                disabled={!date || isInvalidDate}
                color="primary"
                variant="filled"
                size="md"
                onClick={() => {
                  if (date) {
                    const _date = parse(
                      time && showTime ? time.format('HH:mm:ss') : '00:00:00',
                      'HH:mm:ss',
                      date
                    )
                    onChange && onChange(_date)
                    close()
                  }
                }}
              >
                <p className="text-sm-semibold">Apply</p>
              </MyButton>
            </div>
          </footer>
        </div>
      )}
    </MyPopper>
  )
}

function HeaderSelect({ value, onChange, options, ariaLabel }) {
  return (
    <div className="relative">
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => {
          // Years/months are numeric; coerce so setMonth/setFullYear get a number.
          const v = e.target.value
          onChange(isNaN(Number(v)) ? v : Number(v))
        }}
        className="text-sm-semibold cursor-pointer appearance-none rounded-md bg-transparent py-1 pl-2 pr-7 text-gray-light/700 outline-none hover:bg-gray-light/50 focus:bg-gray-light/50"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-1.5 top-1/2 size-4 -translate-y-1/2 text-gray-light/500"
        stroke="currentColor"
      />
    </div>
  )
}

export default MyCalendar
